import EvaluationAnswersReview, {
    QuestionDetail,
} from '@/components/evaluation/EvaluationAnswersReview';
import GlassCard from '@/components/GlassCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { router } from '@inertiajs/react';
import clsx from 'clsx';
import React from 'react';

interface Props {
    attempt: {
        id: number;
        score: number;
        total_score: number;
        passed: boolean;
        completed_at: string | null;
        percentage: number;
    };
    evaluation: {
        id: number;
        title: string;
        passing_score: number;
    };
    questions: QuestionDetail[];
}

function formatDate(iso: string | null): string {
    if (!iso) {
        return '—';
    }
    return new Date(iso).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

const EvaluationDetailContent: React.FC<Props> = ({
    attempt,
    evaluation,
    questions,
}) => {
    const correctCount = questions.filter((q) => q.is_correct === true).length;
    const wrongCount = questions.filter((q) => q.is_correct === false).length;
    const unansweredCount = questions.filter(
        (q) => q.is_correct === null
    ).length;

    return (
        <AppLayout>
            <PageTitle title={evaluation.title} />
            <div className="min-h-screen py-8">
                <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                    <button
                        onClick={() =>
                            router.visit('/my-account/?tab=evaluaciones')
                        }
                        className="mb-6 flex items-center gap-1 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200"
                    >
                        <svg
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                        Mis Evaluaciones
                    </button>

                    <GlassCard className="mb-6 p-6">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    {evaluation.title}
                                </h1>
                                {attempt.completed_at && (
                                    <p className="mt-1 text-sm text-gray-500 dark:text-neutral-400">
                                        Completada el{' '}
                                        {formatDate(attempt.completed_at)}
                                    </p>
                                )}
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="text-right">
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {attempt.percentage}%
                                    </p>
                                    <p className="text-sm text-gray-500 dark:text-neutral-400">
                                        {attempt.score} / {attempt.total_score}{' '}
                                        pts
                                    </p>
                                </div>
                                <span
                                    className={clsx(
                                        'rounded-full px-3 py-1 text-sm font-semibold',
                                        attempt.passed
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                    )}
                                >
                                    {attempt.passed
                                        ? 'Aprobado'
                                        : 'No aprobado'}
                                </span>
                            </div>
                        </div>

                        {questions.length > 0 && (
                            <div className="mt-4 flex gap-4 border-t border-white/20 pt-4 dark:border-white/10">
                                <div className="text-center">
                                    <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                                        {correctCount}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-neutral-400">
                                        Correctas
                                    </p>
                                </div>
                                <div className="text-center">
                                    <p className="text-lg font-semibold text-red-600 dark:text-red-400">
                                        {wrongCount}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-neutral-400">
                                        Incorrectas
                                    </p>
                                </div>
                                {unansweredCount > 0 && (
                                    <div className="text-center">
                                        <p className="text-lg font-semibold text-gray-500 dark:text-neutral-400">
                                            {unansweredCount}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-neutral-400">
                                            Sin respuesta
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </GlassCard>

                    <EvaluationAnswersReview questions={questions} />
                </div>
            </div>
        </AppLayout>
    );
};

const EvaluationDetail: React.FC<Props> = (props) => (
    <ThemeWrapper>
        <EvaluationDetailContent {...props} />
    </ThemeWrapper>
);

export default EvaluationDetail;
